import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "@tanstack/react-router";
import { Crown, Star, Award } from "lucide-react";
import type { MapChef } from "./chef-map-utils";
import type { PremiumTier } from "@/lib/chef-utils";

export { haversineKm, withCoords, sortByDistance } from "./chef-map-utils";
export type { MapChef } from "./chef-map-utils";

/** Sprint 19.1 — marker a tre tier: Gold (oro), Silver (argento), Free (nero). */
export const TIER_MARKER_COLORS: Record<PremiumTier, { fill: string; stroke: string; glow: string }> = {
  gold:   { fill: "#D4AF37", stroke: "#7A5C00", glow: "rgba(212,175,55,.8)" },
  silver: { fill: "#C0C0C0", stroke: "#6B7280", glow: "rgba(192,192,192,.7)" },
  free:   { fill: "#111111", stroke: "#ffffff", glow: "rgba(0,0,0,.4)" },
};

function starIcon(tier: PremiumTier) {
  const { fill, stroke, glow } = TIER_MARKER_COLORS[tier];
  const drop = tier === "free"
    ? `filter: drop-shadow(0 1px 2px ${glow});`
    : `filter: drop-shadow(0 0 6px ${glow});`;
  const html = `
    <div style="transform: translate(-50%, -100%); ${drop}">
      <svg width="34" height="34" viewBox="0 0 24 24" fill="${fill}" stroke="${stroke}" stroke-width="1.2" stroke-linejoin="round">
        <path d="M12 2.5l2.95 6.6 7.05.7-5.3 4.85L18.2 21.5 12 17.8 5.8 21.5l1.5-6.85L2 9.8l7.05-.7z"/>
      </svg>
    </div>`;
  return L.divIcon({
    html,
    className: "chef-star-marker",
    iconSize: [34, 34],
    iconAnchor: [17, 34],
    popupAnchor: [0, -32],
  });
}

const userIcon = L.divIcon({
  html: `<div style="width:18px;height:18px;border-radius:9999px;background:#2563eb;border:3px solid white;box-shadow:0 0 0 2px #2563eb,0 0 12px rgba(37,99,235,.6);"></div>`,
  className: "chef-user-marker",
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

function FlyTo({ position, zoom }: { position: [number, number] | null; zoom: number }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, zoom, { duration: 1.2 });
  }, [position, zoom, map]);
  return null;
}

interface Props {
  chefs: MapChef[];
  height?: string;
  center?: [number, number];
  zoom?: number;
  userPosition?: [number, number] | null;
  showAttribution?: boolean;
}

export function ChefMap({
  chefs,
  height = "100%",
  center = [42.5, 12.5],
  zoom = 6,
  userPosition = null,
  showAttribution = true,
}: Props) {
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);

  if (!ready) {
    return (
      <div
        style={{ height }}
        className="grid w-full place-items-center bg-secondary/40 text-sm text-muted-foreground"
      >
        Caricamento mappa…
      </div>
    );
  }

  return (
    <MapContainer
      center={center}
      zoom={zoom}
      scrollWheelZoom
      style={{ height, width: "100%" }}
      attributionControl={showAttribution}
    >
      <TileLayer
        attribution='&copy; OpenStreetMap'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <FlyTo position={userPosition} zoom={11} />
      {userPosition && (
        <>
          <Marker position={userPosition} icon={userIcon}>
            <Popup>La tua posizione</Popup>
          </Marker>
          <Circle
            center={userPosition}
            radius={20000}
            pathOptions={{ color: "#2563eb", fillOpacity: 0.06, weight: 1 }}
          />
        </>
      )}
      {chefs.map((chef) => (
        <Marker
          key={chef.user_id}
          position={[chef.lat, chef.lng]}
          icon={starIcon(chef.premium_tier)}
        >
          <Popup>
            <div className="flex w-56 gap-3">
              {chef.cover_url ? (
                <img src={chef.cover_url} alt={chef.display_name} className="h-14 w-14 rounded-md object-cover" />
              ) : (
                <div className="grid h-14 w-14 place-items-center rounded-md bg-gray-200 text-sm font-semibold text-gray-600">
                  {chef.display_name.slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5">
                  <strong className="text-sm">{chef.display_name}</strong>
                  {chef.premium_tier === "gold" && (
                    <span className="inline-flex items-center gap-0.5 rounded-sm bg-gradient-to-r from-amber-400 to-yellow-500 px-1 text-[9px] font-bold uppercase tracking-wider text-black">
                      <Crown className="h-2.5 w-2.5" /> Gold
                    </span>
                  )}
                  {chef.premium_tier === "silver" && (
                    <span className="inline-flex items-center gap-0.5 rounded-sm bg-gradient-to-r from-slate-300 to-slate-200 px-1 text-[9px] font-bold uppercase tracking-wider text-slate-900">
                      <Award className="h-2.5 w-2.5" /> Silver
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-600">
                  {chef.city} · {chef.region ?? ""}
                </span>
                {chef.rating_count > 0 ? (
                  <span className="mt-1 inline-flex items-center gap-1 text-xs">
                    <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                    {chef.rating_avg.toFixed(1)} ({chef.rating_count})
                  </span>
                ) : (
                  <span className="mt-1 inline-flex items-center gap-1 text-xs text-gold">
                    ✨ Nuovo su ChefConnect
                  </span>
                )}
                {(chef.min_price ?? chef.hourly_rate) != null && (
                  <span className="text-xs text-gray-600">
                    da €{Math.round((chef.min_price ?? chef.hourly_rate) as number)}/persona
                  </span>
                )}
                <Link
                  to="/chef/$slug"
                  params={{ slug: chef.slug }}
                  className="mt-1.5 text-xs font-medium text-gold hover:underline"
                >
                  Vedi profilo →
                </Link>
              </div>
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}
